import { useState } from 'react';
import {
  Container,
  Grid,
  Paper,
  Typography,
  Box,
  Button,
  IconButton,
  Card,
  CardMedia,
  CardContent,
  Divider,
  TextField,
  Alert,
  CircularProgress,
  Skeleton
} from '@mui/material';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { CartService } from '../services/CartService';
import { Delete as DeleteIcon, Add as AddIcon, Remove as RemoveIcon } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import { useTranslation } from 'react-i18next';
import { ShoppingCart as ShoppingCartIcon } from '@mui/icons-material';

const SHIPPING_THRESHOLD = 100; // Free shipping for orders over $100
const SHIPPING_COST = 10; // Standard shipping cost

export default function Cart() {
  const navigate = useNavigate();
  const { enqueueSnackbar } = useSnackbar();
  const queryClient = useQueryClient();
  const { t } = useTranslation();

  const { data: cart, isLoading, error } = useQuery(
    'cart',
    () => CartService.getCart(),
    {
      refetchOnWindowFocus: false
    }
  );

  const updateQuantityMutation = useMutation(
    ({ productId, quantity }) => CartService.updateQuantity(productId, quantity),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cart');
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  );

  const removeItemMutation = useMutation(
    (productId) => CartService.removeFromCart(productId),
    {
      onSuccess: () => {
        queryClient.invalidateQueries('cart');
        enqueueSnackbar(t('cart.itemRemoved'), { variant: 'success' });
      },
      onError: (error) => {
        enqueueSnackbar(error.message, { variant: 'error' });
      }
    }
  );

  const handleQuantityChange = (productId, newQuantity) => {
    if (newQuantity < 1) return;
    updateQuantityMutation.mutate({ productId, quantity: newQuantity });
  };

  const calculateTotal = () => {
    if (!cart?.items) return 0;
    return cart.items.reduce((total, item) => total + (item.product.price * item.quantity), 0);
  };

  const subtotal = cart?.items?.reduce((total, item) => 
    total + (item.product.price * item.quantity), 0) || 0;

  const shipping = subtotal > SHIPPING_THRESHOLD ? 0 : SHIPPING_COST;

  if (isLoading) {
    return (
      <Box display="flex" justifyContent="center" p={4}>
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return (
      <Alert severity="error" sx={{ m: 2 }}>
        {error.message}
      </Alert>
    );
  }

  if (!cart?.items?.length) {
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box textAlign="center">
          <Typography variant="h5" gutterBottom>
            {t('cart.empty')}
          </Typography>
          <Button
            variant="contained"
            color="primary"
            onClick={() => navigate('/search')}
            sx={{ mt: 2 }}
          >
            {t('cart.startShopping')}
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      <Typography variant="h4" gutterBottom>
        {t('cart.title')}
      </Typography>

      {isLoading ? (
        <Box sx={{ width: '100%' }}>
          {[1, 2, 3].map((n) => (
            <Skeleton key={n} variant="rectangular" height={200} sx={{ mb: 2 }} />
          ))}
        </Box>
      ) : cart?.items?.length === 0 ? (
        <Box textAlign="center" py={4}>
          <ShoppingCartIcon sx={{ fontSize: 60, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            {t('cart.empty')}
          </Typography>
          <Button
            variant="contained"
            onClick={() => navigate('/search')}
            sx={{ mt: 2 }}
          >
            {t('cart.startShopping')}
          </Button>
        </Box>
      ) : (
        <Grid container spacing={4}>
          <Grid item xs={12} md={8}>
            {cart.items.map((item) => (
              <Grid item xs={12} key={item.productId}>
                <Paper sx={{ p: 2 }}>
                  <Grid container spacing={2} alignItems="center">
                    <Grid item xs={12} sm={3}>
                      <CardMedia
                        component="img"
                        height="140"
                        image={item.product?.image || '/placeholder.png'}
                        alt={item.product?.title}
                        sx={{ objectFit: 'contain', borderRadius: 1 }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={9}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                        <Typography variant="h6">{item.product?.title}</Typography>
                        <Typography variant="h6" color="primary">
                          ${(item.product?.price * item.quantity).toFixed(2)}
                        </Typography>
                      </Box>
                      <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', gap: 2 }}>
                        <TextField
                          type="number"
                          label={t('cart.quantity')}
                          value={item.quantity}
                          onChange={(e) => handleQuantityChange(item.productId, parseInt(e.target.value))}
                          InputProps={{ inputProps: { min: 1 } }}
                          size="small"
                          sx={{ width: 100 }}
                        />
                        <Button
                          startIcon={<DeleteIcon />}
                          color="error"
                          onClick={() => removeItemMutation.mutate(item.productId)}
                        >
                          {t('cart.remove')}
                        </Button>
                      </Box>
                    </Grid>
                  </Grid>
                </Paper>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12} md={4}>
            <Paper sx={{ p: 3 }}>
              <Typography variant="h6" gutterBottom>
                {t('cart.summary')}
              </Typography>
              <Box sx={{ my: 2 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>{t('cart.subtotal')}</Typography>
                  <Typography>${calculateTotal().toFixed(2)}</Typography>
                </Box>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography>{t('cart.shipping')}</Typography>
                  <Typography>
                    {shipping === 0 ? t('cart.free') : `$${shipping.toFixed(2)}`}
                  </Typography>
                </Box>
                <Divider sx={{ my: 2 }} />
                <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                  <Typography variant="h6">{t('cart.total')}</Typography>
                  <Typography variant="h6" color="primary">
                    ${(calculateTotal() + shipping).toFixed(2)}
                  </Typography>
                </Box>
              </Box>
              <Button
                fullWidth
                variant="contained"
                size="large"
                onClick={() => navigate('/checkout')}
                sx={{ mt: 2 }}
              >
                {t('cart.checkout')}
              </Button>
            </Paper>
          </Grid>
        </Grid>
      )}
    </Container>
  );
} 